import { useState, useRef, useEffect } from "react";
import * as axios from "axios";
import { SiteVideoData, SiteImageData } from "../../../../interfaces";
import { MediaContentTypes } from "../../../../../constants/contentTypes";
import { VerticalVideoPlayer } from "../../components/VerticalVideoPlayer";
import { VerticalShareArea } from "../../components/VerticalShareArea";
import { SpeedMenuButton } from "../../components/SpeedMenuButton";
import { DesignItemProps } from "../../interfaces";
require("./ynet_site_rylzMediaItem.less");

const DESKTOP_SPEED_OPTIONS = [0.5, 1, 1.5, 2];
const ACTION_BTNS_WIDTH = 65;

export const RylzMediaItemDesktop = (props: DesignItemProps) => {

    const { videoItem, index, domain, languageCode, showPrerollAds, isTitleVisible, isSubTitleVisible, isGalleryView, currentPlayingVideo,
        containerStyle, dynamicHeight, dynamicWidth, onNextSlide } = props;
    const [isSubtitleOpen, setIsSubtitleOpen] = useState(false);
    const [isClamped, setIsClamped] = useState(false);
    const [isPortrait, setIsPortrait] = useState(true);
    const [playerApiRef, setPlayerApiRef] = useState(null) as any;
    const [talkbackCount, setTalkbackCount] = useState<number | undefined>(undefined);
    const [areCommentsBlocked, setAreCommentsBlocked] = useState(false);
    const [isSeeking, setIsSeeking] = useState(false);
    const isSeekingRef: any = useRef(false);
    const wasPlayingRef: any = useRef(false);
    const subtitleRef = useRef(null) as any;

    const item = videoItem as any;
    const { itemId, media, title, subTitle, titleLink } = videoItem;

    const date: string = item.date || "";
    const author: string = item.author || "";
    const labelUrl: string = item.labelUrl || "";
    const labelName: string = item.labelName || "";
    const readMoreTitle: string = item.readMoreTitle || "";
    const labelAuthor: string = item.labelAuthor || "";
    const articleId: string | undefined = item.articleId || undefined;
    const publishedLink: string = item.publishedLink || titleLink || "";
    const imageAutoAdvanceSeconds: number = item.imageAutoAdvanceSeconds || 0;

    const isVideo = media && (media as any).mediaType === MediaContentTypes.MEDIA_VIDEO;
    const isImage = media && (media as any).mediaType === MediaContentTypes.MEDIA_IMAGE;
    const imageUrl = isImage ? (media as SiteImageData).url : "";
    const imageAlt = isImage ? ((media as SiteImageData).alt || title || "") : "";
    const videoPageId = isVideo ? (media as SiteVideoData).videoPageId : "";

    const { processedMedia, videoPublishedLink } = isVideo && media ? (() => {
        const videoData = media as SiteVideoData;
        const url = videoData.url || "";
        const urlWithoutQuery = url.split("?")[0];
        const urlObj = new URL(url, "http://example.com");
        const drmParam = urlObj.searchParams.get("drm");
        const isDrm = drmParam === "1";
        const publishedLinkParam = urlObj.searchParams.get("publishedLink") || "";

        return {
            processedMedia: {
                ...videoData,
                url: urlWithoutQuery,
                is_drm: isDrm
            },
            videoPublishedLink: publishedLinkParam
        };
    })() : { processedMedia: media, videoPublishedLink: "" };

    const shareLink = (isVideo && videoPublishedLink) || publishedLink;

    const PORTRAIT_RATIO_THRESHOLD = 0.75;
    const videoAspectRatio = isVideo ? (media as SiteVideoData).aspectRatio : undefined;

    const lang = languageCode || "";
    const readMoreLabel = lang.includes("en") ? "Read more..." : lang.includes("ru") ? "Читать далее..." : "קראו עוד...";
    const closeLabel = lang.includes("en") ? "Close" : lang.includes("ru") ? "Закрыть" : "צמצום";
    const copyLinkLabel = lang.includes("en") ? "Copy link" : lang.includes("ru") ? "Скопировать ссылку" : "העתק קישור";
    const linkCopiedLabel = lang.includes("en") ? "Link copied to clipboard" : lang.includes("ru") ? "Ссылка скопирована" : "הקישור הועתק ללוח";

    const subtitleText = item.text || subTitle || "";
    const itemType = isVideo ? "video-item" : isImage ? "image-item" : "";
    const aspectClass = isPortrait ? "portrait-media" : "landscape-media";
    const itemClass = `rylz-media-item-desktop vertical-video-item ${itemType} ${isSubtitleOpen ? "openMode" : "standardMode"} ${isSeeking ? "is-seeking" : ""}`;
    const shareItemType = isImage ? "rylz_image" : isVideo ? "rylz_video" : "rylz_item";
    const isCurrentPlaying = !isGalleryView || currentPlayingVideo === index;
    const domId = `VerticalVideoItem_rylzMediaItemDesktop_${itemId}`;
    const itemStyle: React.CSSProperties = {
        ...(containerStyle || {}),
        width: dynamicWidth ? dynamicWidth + ACTION_BTNS_WIDTH : (containerStyle && (containerStyle as any).width),
    };
    const mediaStyle: React.CSSProperties = {
        width: dynamicWidth || "100%",
        height: "100%",
    };

    useEffect(() => {
        if (isVideo && videoAspectRatio) {
            const [w, h] = videoAspectRatio.split(":").map(Number);
            setIsPortrait(w / h <= PORTRAIT_RATIO_THRESHOLD);
        }
    }, [isVideo, videoAspectRatio]);

    useEffect(() => {
        const el = subtitleRef.current;
        if (!el || isSubtitleOpen) return;

        const measure = () => {
            // -webkit-line-clamp often reports scrollHeight > clientHeight even when
            // text fits; measure natural height without clamp instead.
            const wasClosed = el.classList.contains("close");
            if (wasClosed) {
                el.classList.remove("close");
            }
            const fullHeight = el.scrollHeight;
            if (wasClosed) {
                el.classList.add("close");
            }

            const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 24;
            setIsClamped(fullHeight > lineHeight * 2 + 2);
        };

        const raf = requestAnimationFrame(measure);
        return () => cancelAnimationFrame(raf);
    }, [subTitle, item.text, isSubtitleOpen]);

    useEffect(() => {
        if (!articleId) return;
        axios.get(`/iphone/json/api/talkbacks/list/v2/${articleId}/start_to_end/0`)
            .then((res: any) => {
                if (res.data && res.data.rss && res.data.rss.channel) {
                    const channel = res.data.rss.channel;
                    if (channel.talkbacks_closed === 1 || channel.talkbacksClosed === 1) {
                        setAreCommentsBlocked(true);
                        return;
                    }
                    setTalkbackCount(channel.sum_talkbacks);
                }
            })
            .catch(() => { });
    }, [articleId]);

    useEffect(() => {
        if (!isImage || !isCurrentPlaying || !onNextSlide) return;
        if (!imageAutoAdvanceSeconds || imageAutoAdvanceSeconds <= 0) return;

        const timer = window.setTimeout(() => {
            onNextSlide();
        }, imageAutoAdvanceSeconds * 1000);

        return () => window.clearTimeout(timer);
    }, [isImage, isCurrentPlaying, imageAutoAdvanceSeconds, itemId]);

    useEffect(() => {
        if (!isVideo) return;

        const container = document.getElementById(domId);
        if (!container) return;

        let seekTimeout: number | undefined;
        let isMounted = true;

        const getVideoElement = (): HTMLVideoElement | null => {
            const root = document.getElementById(domId);
            if (!root) return null;
            const engine = root.querySelector("#player .fp-engine") as HTMLVideoElement;
            return engine && engine.tagName === "VIDEO" ? engine : null;
        };

        const onSeekStart = () => {
            if (!isMounted || isSeekingRef.current) return;
            isSeekingRef.current = true;
            if (seekTimeout) { clearTimeout(seekTimeout); seekTimeout = undefined; }

            const videoEl = getVideoElement();
            if (videoEl) {
                wasPlayingRef.current = !videoEl.paused;
                if (!videoEl.paused) videoEl.pause();
            }

            setIsSeeking(true);
        };

        const onSeekEnd = () => {
            if (!isMounted || !isSeekingRef.current) return;
            isSeekingRef.current = false;
            if (seekTimeout) clearTimeout(seekTimeout);

            if (wasPlayingRef.current) {
                const videoEl = getVideoElement();
                if (videoEl) {
                    const p: any = videoEl.play();
                    if (p && typeof p.catch === "function") p.catch(() => { });
                }
                wasPlayingRef.current = false;
            }

            seekTimeout = window.setTimeout(() => {
                if (isMounted) setIsSeeking(false);
            }, 800);
        };

        const onPlayerDetailsMouseDown = (e: MouseEvent) => {
            e.stopPropagation();
            onSeekStart();
        };

        const playerDetails = container.querySelector(".player-details") as HTMLElement | null;
        if (playerDetails) {
            playerDetails.addEventListener("mousedown", onPlayerDetailsMouseDown);
        }
        document.addEventListener("mouseup", onSeekEnd);

        return () => {
            isMounted = false;
            if (seekTimeout) clearTimeout(seekTimeout);
            if (playerDetails) {
                playerDetails.removeEventListener("mousedown", onPlayerDetailsMouseDown);
            }
            document.removeEventListener("mouseup", onSeekEnd);
        };
    }, [isVideo, itemId]);

    useEffect(() => {
        if (!isVideo) return;

        const container = document.getElementById(domId);
        if (!container) return;

        const clearTimelineTitle = () => {
            const timeline = container.querySelector(".timeline .fp-timeline") as HTMLElement | null;
            if (timeline && timeline.hasAttribute("title")) {
                timeline.removeAttribute("title");
            }
        };

        clearTimelineTitle();
        const observer = new MutationObserver(clearTimelineTitle);
        observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ["title"] });

        return () => observer.disconnect();
    }, [isVideo, itemId]);

    const onImageLoad = (e: any) => {
        const img = e.target;
        const ratio = img.naturalWidth / img.naturalHeight;
        setIsPortrait(ratio <= PORTRAIT_RATIO_THRESHOLD);
    };

    const formatCount = (count: number | undefined): string => {
        if (count === undefined || count === null) return "";
        if (count >= 10000) return `${Math.round(count / 1000)}K`;
        if (count >= 1000) {
            const value = count / 1000;
            const rounded = Math.round(value * 10) / 10;
            return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)}K`;
        }
        return count.toString();
    };

    const toggleSubtitle = () => {
        if (!isClamped) return;
        setIsSubtitleOpen(!isSubtitleOpen);
    };

    const renderLabel = () => {
        if (labelAuthor) {
            return (
                <div className="rylz-label commentary-label">
                    {labelUrl &&
                        <span className="author-image-wrapper">
                            <img className="author-image" src={labelUrl} alt={labelAuthor} />
                        </span>
                    }
                    <span className="label-text">
                        <div className="title" dangerouslySetInnerHTML={{ __html: labelName || "פרשנות" }}></div>
                        <div className="author" dangerouslySetInnerHTML={{ __html: labelAuthor }}></div>
                    </span>
                </div>
            );
        }

        if (labelUrl) {
            return (
                <div className="rylz-label logo-label">
                    <img className="logo-image" src={labelUrl} alt={labelName} />
                </div>
            );
        }

        if (labelName) {
            return (
                <div className="rylz-label text-label">
                    <span className="label-text" dangerouslySetInnerHTML={{ __html: labelName }} {...(isSubTitleVisible && subtitleText ? { onClick: toggleSubtitle } : {})}></span>
                </div>
            );
        }

        return null;
    };

    return (
        <div className={itemClass} id={domId} style={itemStyle}>
            <div className="media-column" style={mediaStyle}>
                <div className={`media-wrapper ${aspectClass}`}>
                    {isVideo &&
                        <VerticalVideoPlayer
                            index={index}
                            isRylz={true}
                            isMobile={false}
                            onNextSlide={onNextSlide}
                            dynamicWidth={dynamicWidth}
                            dynamicHeight={dynamicHeight}
                            isGalleryView={isGalleryView}
                            showPrerollAds={showPrerollAds}
                            prerollIndex={videoItem.prerollIndex}
                            media={processedMedia as SiteVideoData}
                            itemId={`rylzMediaItemDesktop_${itemId}`}
                            hidePrerollAds={videoItem.hidePrerollAds}
                            currentPlayingVideo={currentPlayingVideo}
                            onPlayerApiReady={(ref) => setPlayerApiRef(ref)}
                        />
                    }

                    {isImage &&
                        <img className="rylz-image" src={imageUrl} alt={imageAlt} onLoad={onImageLoad} />
                    }
                </div>

                <div className="content-div">
                    <div className="text-content">
                        {renderLabel()}

                        {isTitleVisible &&
                            <div className="title" dangerouslySetInnerHTML={{ __html: title || "" }} {...(isSubTitleVisible && subtitleText ? { onClick: toggleSubtitle } : {})} />
                        }

                        {isSubTitleVisible && subtitleText &&
                            <div
                                className={`subtitle ${isSubtitleOpen ? "open" : "close"}`}
                                onClick={isClamped ? toggleSubtitle : undefined}
                                style={isClamped ? undefined : { cursor: "default" }}
                            >
                                <div
                                    className={`subtitle-body ${isSubtitleOpen ? "" : "close"}`}
                                    ref={subtitleRef}
                                    dangerouslySetInnerHTML={{ __html: subtitleText }}
                                />

                                {isClamped &&
                                    <span className={`read-more ${isSubtitleOpen ? "close-label" : ""}`}>
                                        {isSubtitleOpen ? closeLabel : readMoreLabel}
                                    </span>
                                }
                            </div>
                        }

                        {(author || date) &&
                            <div className="author-date">
                                {author && !labelAuthor && <span className="author" dangerouslySetInnerHTML={{ __html: author }} />}
                                {author && !labelAuthor && date && <span className="separator"> | </span>}
                                {date && <span className="date">{date}</span>}
                            </div>
                        }

                        {publishedLink && readMoreTitle &&
                            <a className="full-article-link" href={publishedLink} onClick={(e) => e.stopPropagation()}>
                                <span className="link-text" dangerouslySetInnerHTML={{ __html: readMoreTitle }}></span>
                                <span className="link-arrow"></span>
                            </a>
                        }
                    </div>

                    {isVideo && (
                        <div className="player-details">
                            <div className="timeline" id="timeline-target"></div>
                            <div className="seek-time-display">
                                <span className="elapsed" id="elapsed-target"></span>
                                <span className="separator">/</span>
                                <span className="duration" id="duration-target"></span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="btns-content">
                {isVideo && playerApiRef &&
                    <SpeedMenuButton cycleOnTap playerApiRef={playerApiRef} speedOptions={DESKTOP_SPEED_OPTIONS} />
                }

                {isVideo && <div className="mute-btn" id="volume-target"></div>}

                {articleId && !areCommentsBlocked &&
                    <a className="talkback-btn" href={publishedLink ? `${publishedLink}#commentsSection` : undefined}>
                        <span className="talkback-icon"></span>
                        {talkbackCount !== undefined && talkbackCount > 0 && <span className="talkback-count">{formatCount(talkbackCount)}</span>}
                    </a>
                }

                {shareLink &&
                    <VerticalShareArea
                        domain={domain}
                        link={shareLink}
                        isMobile={false}
                        title={title || ""}
                        showLabels={false}
                        closeOnShareClick={false}
                        closeOnOutsideClick={true}
                        hideTriggerWhenOpen={true}
                        copyLinkLabel={copyLinkLabel}
                        videoPageId={videoPageId || ""}
                        articleId={articleId || itemId}
                        linkCopiedLabel={linkCopiedLabel}
                        analyticShareLabel={shareItemType}
                        shareButtons={["wa", "fb", "tw", "mail", "copy"]}
                        shareItemType={videoPageId ? "verticalVideo" : "rylz"}
                    />
                }
            </div>
        </div>
    );
};
